/**
 * @author Fernando Ruano
 * @file routes.js
 * @description Dashboard routes
 */

// Dependencies
import {
  SwapOutlined,
  MedicineBoxOutlined,
  AppstoreOutlined,
  TeamOutlined,
  IdcardOutlined,
  ReadOutlined,
  MinusOutlined,
  ScheduleOutlined,
  LineChartOutlined,
  AppleFilled,
  UserOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  FormOutlined,
  PushpinOutlined,
  SafetyOutlined,
  SolutionOutlined,
  PartitionOutlined,
  CalendarOutlined
} from '@ant-design/icons';

// Pages
import DashboardDetail from '@nephro/containers/DashboardDetail';
import Appointments from '@nephro/containers/Appointments';
import ComponentsState from '@nephro/pages/ComponentsState';
import DashboardStaff from '@nephro/pages/DashboardStaff';
import MyStage from '@nephro/pages/Indicators/MyStage';
import KidneyProgress from '@nephro/pages/Indicators/KidneyProgress';
import PatientRecords from '@nephro/pages/PatientRecords';
import BloodPressure from '@nephro/pages/Indicators/BloodPressure';
import CountryUserRolesContainer from '@nephro/containers/CountryUserRoles';
import PatientsContainer from '@nephro/containers/Patients';
import PatientsRecordsContainer from '@nephro/containers/PatientsRecords';
import RolesContainer from '@nephro/containers/Roles';
import ProfilesContainer from '@nephro/containers/Profiles';
import ParametersContainer from '@nephro/containers/Parameters';
import MedicalParametersContainer from '@nephro/containers/MedicalParameters';
import EducationalMaterial from '@nephro/containers/EducationalMaterial';
import VideoCallsContainer from '@nephro/containers/VideoCalls';
import VideoCallsFrameContainer from '@nephro/containers/VideoCallsFrame';
import DashboardPatient from '@nephro/pages/DashboardPatient';
import Adherence from '@nephro/pages/Indicators/Adherence';
import Nutritional from '@nephro/pages/Indicators/Nutritional';
import BloodGlucose from '@nephro/pages/Indicators/BloodGlucose';
import NephroPlusChat from '@nephro/pages/NephroPlusChat';
import PatientEvents from '@nephro/containers/PatientEvents';
import MyLabs from '@nephro/pages/MyLabs';
import MyProfile from '@nephro/containers/PatientProfile';
import MyRecommendations from '@nephro/containers/Recommendations';
import About from '@nephro/pages/About';
import TermsAndConditions from '@nephro/pages/About/TermsAndConditions';
import Privacy from '@nephro/pages/About/Privacy';
import ScheduledAppointments from '@nephro/pages/ScheduledAppointments';

const pathStart = '/main';
const routes = (isPatient) => [
  {
    path: `${pathStart}/dashboard`,
    isProtected: true,
    component: isPatient ? DashboardPatient : DashboardStaff,
    intlId: isPatient ? 'dashboard.myDashboard' : 'dashboard.dashboard',
    icon: <AppstoreOutlined />
  },
  {
    path: `${pathStart}/my-profile`,
    isProtected: true,
    component: MyProfile,
    intlId: 'dashboard.myProfile',
    icon: <UserOutlined />
  },
  {
    path: `${pathStart}/patients`,
    isProtected: true,
    component: PatientsContainer,
    intlId: 'dashboard.patients',
    icon: <SolutionOutlined />
  },
  {
    path: `${pathStart}/stages`,
    isProtected: true,
    component: MyStage,
    intlId: 'dashboard.stage',
    icon: <LineChartOutlined />
  },
  {
    path: `${pathStart}/rol-management`,
    isProtected: true,
    component: RolesContainer,
    intlId: 'dashboard.roles',
    icon: <SwapOutlined />
  },
  {
    path: `${pathStart}/systems-parameters`,
    isProtected: true,
    component: ParametersContainer,
    intlId: 'dashboard.paramSystem',
    icon: <PartitionOutlined />
  },
  {
    path: `${pathStart}/medical-parameters`,
    isProtected: true,
    component: MedicalParametersContainer,
    intlId: 'dashboard.paramMedical',
    icon: <MedicineBoxOutlined />
  },
  {
    path: `${pathStart}/patient_records`,
    isProtected: true,
    component: PatientsRecordsContainer,
    intlId: 'dashboard.registerPatients',
    icon: <FormOutlined />
  },
  {
    path: `${pathStart}/users-management`,
    isProtected: true,
    component: CountryUserRolesContainer,
    intlId: 'dashboard.users',
    icon: <TeamOutlined />
  },
  {
    path: `${pathStart}/profiles`,
    isProtected: true,
    component: ProfilesContainer,
    intlId: 'dashboard.profiles',
    icon: <IdcardOutlined />
  },
  {
    path: `${pathStart}/components-state`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.item1',
    icon: <SafetyOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/educational-material`,
    isProtected: true,
    component: EducationalMaterial,
    intlId: isPatient ? 'dashboard.ITakeCare' : 'dashboard.educationalMaterial',
    icon: <ReadOutlined />
  },
  // OTHER MENU ITEMS
  {
    path: `${pathStart}/appointment-and-calendars`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.appointsCalendar',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/reports`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.reports',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/parameters`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.params',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/clinic-history`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.clinicHistory',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/blood-presure`,
    isProtected: true,
    component: BloodPressure,
    intlId: 'dashboard.bloodPressure',
    icon: <LineChartOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/my-status`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.myStatus',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-stages`,
    isProtected: true,
    component: MyStage,
    intlId: 'dashboard.stage',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-blood-presure`,
    isProtected: true,
    component: PatientRecords,
    intlId: 'dashboard.myRecords',
    icon: <FormOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-blood-glucose`,
    isProtected: true,
    component: BloodGlucose,
    intlId: 'dashboard.bloodGlucose',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-my-metrics`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.myMetrics',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-nutritional`,
    isProtected: true,
    component: Nutritional,
    intlId: 'dashboard.nutritionalClass',
    icon: <AppleFilled />,
    exact: true
  },
  {
    path: `${pathStart}/patient-adherence`,
    isProtected: true,
    component: Adherence,
    intlId: 'dashboard.adherence',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/patient-my-recomendations`,
    isProtected: true,
    component: MyRecommendations,
    intlId: 'dashboard.myRecommendations',
    icon: <PushpinOutlined />
  },
  {
    path: `${pathStart}/appointments`,
    isProtected: true,
    component: Appointments,
    intlId: 'dashboard.myAppointments',
    icon: <ScheduleOutlined />
  },
  {
    path: `${pathStart}/appointments`,
    isProtected: true,
    component: Appointments,
    intlId: 'dashboard.myAppointments',
    icon: <ScheduleOutlined />
  },
  {
    path: `${pathStart}/scheduled-appointments`,
    isProtected: true,
    component: ScheduledAppointments,
    intlId: '',
    icon: <CalendarOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/exams`,
    isProtected: true,
    component: MyLabs,
    intlId: 'dashboard.myLabs',
    icon: <ExperimentOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/contact`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.contact',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/about`,
    isProtected: true,
    component: About,
    intlId: 'dashboard.aboutApp',
    icon: <InfoCircleOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/my-profile`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.myProfile',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/help`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.help',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/risk`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.risk',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/vaccination-status`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.vaccinationSts',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/kidney-progression`,
    isProtected: true,
    component: KidneyProgress,
    intlId: 'dashboard.renalProgress',
    icon: <LineChartOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/access`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.access',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/protein-wear`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.proteinWear',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/nutritional-indicator`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.nutritionalInd',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/education`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.education',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/conservative-handle`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.preservedHand',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/psicosocial-profile`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.psychosocialProf',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/psicological-valoration`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.psychologicalEval',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/events`,
    isProtected: true,
    component: PatientEvents,
    intlId: 'dashboard.myEvents',
    icon: <MedicineBoxOutlined />
  },
  {
    path: `${pathStart}/clinic-parameters`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.clinicParams',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/metabolic-diabetic`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.metabolicDiab',
    icon: <MinusOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/videocall`,
    isProtected: true,
    component: VideoCallsContainer,
    intlId: 'dashboard.videoCalls'
  },
  {
    path: `${pathStart}/videocalls-frame`,
    isProtected: true,
    component: VideoCallsFrameContainer,
    intlId: 'dashboard.videoCalls'
  },
  {
    path: `${pathStart}/dashboard-detail`,
    isProtected: true,
    component: DashboardDetail,
    intlId: 'dashboard.riskClasification'
  },
  {
    path: `${pathStart}/nephro-plus-chat`,
    isProtected: true,
    component: NephroPlusChat,
    intlId: 'dashboard.nephroPlusChat'
  },
  {
    path: `${pathStart}/status`,
    isProtected: true,
    component: ComponentsState,
    intlId: 'dashboard.status',
    icon: <SafetyOutlined />,
    exact: true
  },
  {
    path: `${pathStart}/terms-and-conditions`,
    isProtected: true,
    component: TermsAndConditions,
    intlId: 'dashboard.status',
    exact: true
  },
  {
    path: `${pathStart}/privacy`,
    isProtected: true,
    component: Privacy,
    intlId: 'Política de privacidad',
    exact: true
  },
  {
    redirect: true,
    from: '/main',
    to: `${pathStart}/dashboard`
  }
];

export default routes;
